/**
 * DemoFirstRunRail — the scripted arc a demo prospect meets on first login.
 *
 *   welcome -> problem -> inputs -> choose -> promise -> replay -> reveal
 *           -> proof -> ask -> repeat -> closing
 *
 * The payload comes from `me.demo.first_run` (the demo config blob's
 * `demo_first_run` key — the same workspace-settings channel that already
 * carries `tour` and `welcome_message`), validated by ./payload.ts. No
 * payload, a malformed one, or `enabled: false` and this renders nothing at
 * all: older demos are untouched. The parent (DemoFirstRunChrome in
 * ../DemoChrome.tsx) gates on `me.demo`, so normal deployments never mount it.
 *
 * ORIENT BEFORE CHOICE. `problem` (or, for a demo authored before it, `what`)
 * and `inputs` open the arc because a real walkthrough proved the old opening
 * didn't: a prospect met a welcome line and then a menu of three workflows,
 * with no idea what the product was, that the client was invented, or what data
 * any of it would run on. `inputs` shows the actual files, and only then does
 * `choose` ask for a decision. Every beat is skipped whole when the payload
 * doesn't carry it, so demos authored before these keys existed play exactly
 * the arc they always did.
 *
 * OPEN ON THEIR PROBLEM, NOT OUR PRODUCT. The redesign's spine. `what` explains
 * Chaidojo, which is the seller's subject and the wrong first screen; `problem`
 * names the pain the prospect already has (`context.pain_line`) and releases it
 * with what they are about to get (`expect_line`). When a demo authors a pain
 * line the product explanation is DEMOTED out of the rail entirely — it lives
 * on the sign-in page — and three pieces of copy move with it:
 *   - `expect_line` moves from under the welcome into `problem`, as the release
 *     (./machine.ts `expectLineHost`);
 *   - the fiction disclosure moves from `what` into the `inputs` note, beside
 *     the invented files it is about (`fictionLineHost`);
 *   - `choose` stops being a menu: the live-build card leads alone, and the
 *     others are behind a quiet toggle. One workflow leads.
 * With no pain line authored, all three stay exactly where they were.
 *
 * OUTCOME BEFORE ARTEFACT. The reveal used to lead with provenance — "this file
 * did not exist when you clicked" — which is the most interesting sentence in
 * the room to the people who built it and the least interesting to the person
 * being sold to. When the build carries an `outcome` (the server's own
 * formatted stats plus the decision they imply), the reveal leads with THAT,
 * the provenance line demotes to a caption, and the deliverable opens on an
 * explicit "Open the pack" rather than sliding in underneath. With no outcome,
 * the old reveal — auto-navigation included — is untouched.
 *
 * THEN LET THEM CHALLENGE A NUMBER. `proof` is the trust beat — the permission
 * ladder's "can I trust it?" rung, which used to live only in the second-visit
 * tour. It plays right after the reveal, while the deliverable's own page is
 * behind the panel, and invites one real click: the evidence marker after any
 * figure, which names the source file. The rail has no scrim precisely so this
 * click can actually land. Platform-gated (`payload.proof`), so existing demos
 * keep their arc until they are re-reconciled.
 *
 * AND THEN IT KEEPS RUNNING. `repeat` is the last scripted beat: the run just
 * watched, drawn as a standing job with an owner, an output and a cadence. The
 * schedule shown is DISPLAY ONLY and the beat says so in words — nothing in a
 * demo room is on anyone's calendar.
 *
 * SHAPE: a bottom-centred panel, NOT a modal — deliberately no scrim. The
 * last two states deep-link into the prospect's own deliverable and composer,
 * and a dimmed app behind a marketing overlay would bury exactly the thing the
 * arc is pointing at.
 *
 * LOOK: DARK, unconditionally (../chromeTheme.ts). The app is light; the
 * narrator is not. That is the whole point — a prospect must never have to
 * work out whether a thing on screen is the product or the pitch. The panel
 * also wears a "DEMO GUIDE" micro-badge, so the distinction is stated and not
 * merely implied.
 *
 * MOUNT POINT: routes/__root.tsx, above both the `_app` layout and /manage —
 * same reasoning as DemoTour: the arc navigates, and a rail mounted inside
 * `_app` would unmount mid-story on a layout swap.
 *
 * The rail SUPPRESSES the guided tour's auto-start for the whole session it
 * plays in (see DemoTour.tsx) — two overlays opening on the same first paint
 * is no arc at all. The handoff is explicit: the closing strip's "See how it's
 * made" starts the tour now; otherwise the tour's auto-start resumes on the
 * NEXT login, when the rail is seen and stands down.
 *
 * THE LIVE BUILD. One card in a demo may be marked `live_build`. Choosing it
 * turns the replay state from a RECORDING into a BUILD: the rail fires
 * POST /demo/live-build on entry and the server instantiates a parameterized
 * deck from the demo's authored facts, compiles it, and attaches it as a real
 * artifact version — with no model anywhere in that path. The reveal then
 * opens a file that did not exist when the prospect clicked.
 *
 * Two honesty rules govern that state, and they are the whole reason it is
 * worth building:
 *   - every duration shown is the SERVER's own measurement of a step it
 *     actually ran. The rail never invents a step and never invents a number;
 *     the stagger between lines is a reading pace, and each line carries the
 *     real cost of the work it names.
 *   - a failed build degrades to the ordinary replay and SAYS SO ("the live
 *     build didn't run — this is the provisioning run's log"). A rail that
 *     quietly showed the recording while claiming a live build would be
 *     making exactly the claim this feature exists to stop making.
 *
 * THE BUILD IS CACHED AFTER THE FIRST SEAT — and every seat after that used to
 * get one flat line where the build had been, which is the best sixty seconds
 * of the arc collapsed into a sentence. So the platform records the first real
 * run (`live_build_recording`) and later seats WATCH THAT, paced from its own
 * per-step timings, under a kicker that says it is a recording. The three
 * cases stay separate all the way to the reveal headline: a fresh build says
 * the file did not exist when you clicked; a recorded run says this is the run
 * that made it; a cached build with no recording keeps the old one-liner.
 *
 * REPLAY MODE (`mode: "replay"`, entered from the sidebar's "Replay the
 * intro"): the same arc, ignoring the seen-mark and the attempt cap, writing
 * no marks and reporting no beacons (see ./seen.ts), and never firing a real
 * build — the live-build card plays the recording or degrades honestly.
 *
 * Accessibility: `prefers-reduced-motion` turns the replay into an
 * instantly-complete log (no pacing, no animation) — and the live build's
 * steps likewise land complete the moment the server answers. Esc skips to the
 * closing strip (and dismisses from there), Enter advances — both ignored
 * while the prospect is typing in the app behind the rail.
 */

import { useRouter } from "@tanstack/react-router";
import { CheckIcon, ExternalLinkIcon } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { type LiveBuildResult, runLiveBuild } from "@/api/demo";
import {
  onComposerSent,
  prefillComposer,
  requestAgentPanel,
  requestComposerSubmit,
  SEND_TTL_MS,
  sendThroughComposer,
} from "@/lib/composerBus";
import { postDemoEvent } from "@/lib/demoEvents";

import {
  DEMO_BADGE,
  DEMO_CARD,
  DEMO_CHIP,
  DEMO_CHIP_BUTTON,
  DEMO_CHIP_BUTTON_SM,
  DEMO_DELTA,
  DEMO_EYEBROW,
  DEMO_GHOST_BTN,
  DEMO_LIVE_CHIP,
  DEMO_RECORDED_CHIP,
  DEMO_LOG,
  DEMO_PANEL,
  DEMO_PRIMARY_BTN,
  DEMO_SUBTLE_BTN,
  DEMO_WELL,
  demoChromeVars,
} from "../chromeTheme";
import { startDemoTour } from "../DemoTour";
import { announceGuideOpened, onGuideOpened, setGuideOpen } from "../guideOverlays";
import {
  type AskHandoff,
  type FirstRunState,
  type ScreenRect,
  askBeat,
  askHandoff,
  buildTimeline,
  expectLineHost,
  fictionLineHost,
  firstState,
  nextState,
  prevState,
  recordedStepDelaysMs,
  replayFor,
  replayIntervalMs,
  skipTarget,
  stepCount,
  stepIndex,
} from "./machine";
import {
  FIRST_RUN_MAX_FILES,
  type FirstRunCard,
  type FirstRunOutcome,
  type FirstRunPayload,
  type FirstRunRecordedStep,
  parseFirstRunOutcome,
} from "./payload";
import { curateReplayLines } from "./replayLines";
import {
  type FirstRunMode,
  firstRunReports,
  markFirstRunSeen,
  noteFirstRunAttempt,
} from "./seen";

/** Window event that opens the rail in replay mode. */
const FIRST_RUN_REPLAY_EVENT = "chaidojo:start-first-run";

/**
 * Replay the intro from anywhere (the sidebar affordance) — the same idiom as
 * `startDemoTour`, and the answer to "somebody who skimmed the first flow can
 * never take that path again."
 */
export function startDemoFirstRun(): void {
  window.dispatchEvent(new CustomEvent(FIRST_RUN_REPLAY_EVENT));
}

/** Subscribe to replay requests. Exported for ../DemoChrome.tsx, which owns
 *  the mount and therefore has to be the thing that hears the event. */
export function onDemoFirstRunReplay(handler: () => void): () => void {
  window.addEventListener(FIRST_RUN_REPLAY_EVENT, handler);
  return () => window.removeEventListener(FIRST_RUN_REPLAY_EVENT, handler);
}

/** The replay's kicker. Fixed copy, deliberately hardcoded: it is the claim
 *  the whole arc rests on, and a demo author must not be able to soften it.
 *  It names the client's files rather than "your data room" — a phrase a lay
 *  prospect does not necessarily own. */
function replayKicker(client: string): string {
  return `The run we did on ${possessive(client, "your files")} — every line is a check that actually ran.`;
}

/** The LIVE build's kicker. Hardcoded for the same reason, and it is a
 *  strictly bigger claim, so it may only ever appear when the server has
 *  actually answered — while the request is still in flight the rail says
 *  what is happening, not what has happened. */
const LIVE_KICKER = "Built just now, from your facts — no model in the loop.";

/** What the rail says WHILE the live build is in flight. Present tense, and a
 *  strictly smaller claim than LIVE_KICKER: nothing has been built yet. */
const LIVE_RUNNING_KICKER = "Building it from your facts, right now — no model in the loop.";

/** The RECORDED build's kicker. The claim is deliberately one notch smaller
 *  than LIVE_KICKER, and says the word "recorded" in the first line, because
 *  what follows looks exactly like a live build and must not be mistaken for
 *  one. */
function recordedKicker(client: string): string {
  return `The run on ${possessive(client, "these files")} — recorded.`;
}

/** What the rail says when the live build didn't run. The prospect is looking
 *  at the provisioning log instead, and is told so. */
const LIVE_FALLBACK = "The live build didn't run — this is the provisioning run's log.";

/** The same, for a replay: nothing failed, we simply don't re-run a build to
 *  show someone the intro a second time. */
const REPLAY_FALLBACK =
  "This is the intro again, so nothing is re-run — you're seeing the provisioning run's log.";

/** Reading pace between completed live-build steps. Not a claim about how
 *  long anything took (each line carries its own real ms) — just slow enough
 *  that three lines don't arrive as one flash. */
const LIVE_STEP_STAGGER_MS = 320;

/** The outcome card's column count, by how many stats there are. A LOOKUP of
 *  literal classes, not a computed string: Tailwind generates what it can see
 *  in the source. Two columns on a narrow panel either way, so four stats
 *  never squeeze to unreadable. */
const STAT_COLUMNS: Record<number, string> = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4",
};

/** The workflow shape every demo's repeat beat draws. Deliberately generic and
 *  hardcoded: it is the shape of the thing, not a claim about this demo. */
const REPEAT_PIPELINE = "Data → Analysis → Review → Deliverable";

/** Said plainly on the repeat beat, because a rendered schedule row looks
 *  exactly like a scheduled job and this one is a picture of one. */
const REPEAT_HONESTY =
  "Scheduling is illustrative in this demo room — a live deployment runs this on your calendar.";

/** An authored sentence with a full stop on it, so the rail's own sentence can
 *  follow it in the same paragraph. Authors end lines with and without one. */
function endStopped(line: string): string {
  const text = line.trim();
  return /[.!?…]$/.test(text) ? text : `${text}.`;
}

/** `Acme's` / `Acme Corp's` — or `fallback` when there is no client name. */
function possessive(client: string, fallback: string): string {
  const name = client.trim();
  if (!name) return fallback;
  return `${name}${name.endsWith("s") ? "'" : "'s"} files`;
}

/** The live build's state machine. `steps` are only ever the SERVER's —
 *  measured live, or measured live and recorded. */
/** The live build's state machine. `cached` is a DONE answer whose file
 *  already existed and for which no recording survives — the one path that
 *  used to collapse the build beat into a single flat line ("was already
 *  built from these figures", audited live by the user). It renders the
 *  room's curated provisioning log instead, so every seat watches real
 *  workflow events, and keeps the result for the reveal. */
type LiveBuild =
  | { phase: "idle" }
  | { phase: "running" }
  | { phase: "done"; result: LiveBuildResult }
  | { phase: "recorded" }
  | { phase: "cached"; result: LiveBuildResult }
  | { phase: "failed" };

/** Seconds, one decimal — the unit a build is talked about in. */
/** What the open button calls the deliverable. "Pack" is board-deck
 *  language and used to be the only word the button knew — an authored
 *  memo's reveal would say "Open the pack" over a DOCX. The format rides
 *  the /me payload (a live build is implied pptx: it only makes decks). */
function openPackLabel(format: string): string {
  switch (format) {
    case "pptx":
      return "Open the pack";
    case "docx":
      return "Open the document";
    case "pdf":
      return "Open the report";
    case "xlsx":
      return "Open the workbook";
    default:
      return "Open the deliverable";
  }
}

function secs(ms: number): string {
  return `${(Math.max(ms, 0) / 1000).toFixed(1)}s`;
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false; // no matchMedia (jsdom, old browsers) -> animate as normal
  }
}

/** An element's screen rect, or null when there is no element. */
function rectOf(el: Element | null): ScreenRect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
}

/** True when a keystroke belongs to whatever the prospect is typing into,
 *  not to the rail. Enter/Esc must never hijack the app's own composer. */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") return false;
  const tag = el.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
}

/**
 * One workflow card, in either of the `choose` beat's two layouts.
 *
 * `hero` is the card leading its screen alone: same card, full width, one type
 * step up.
 *
 * `onPick` is what makes a card a CHOICE. In the hero layout the other
 * examples render without one — as plain cards, not buttons — because the
 * whole downstream arc (the promise, the reveal, the repeat beat) is staged
 * around the hero workflow, and a card that advances the arc while every
 * later screen describes a different workflow is a choice in appearance only
 * (audited live: picking the memo card promised, revealed and scheduled the
 * committee deck). The other examples are real workflows in the room — the
 * caption under them says where they live — they are just not this arc.
 */
function ChooseCard({
  card,
  liveChip,
  otherChip = "recorded run",
  hero = false,
  pressed = false,
  onPick,
}: {
  card: FirstRunCard;
  /** What the live card's chip promises — computed by the parent, which
   *  knows the seat's mode and whether a recording exists. The chip is a
   *  PROMISE about the very next state, so it must be right per path:
   *  "builds it live" for a fresh arc, "the run, recorded" once a recording
   *  exists, and never a live claim inside a replay (which fires no build —
   *  audited live: a post-reconcile replay wore "builds it live" one screen
   *  before "nothing is re-run"). */
  liveChip: string;
  /** The NON-live card's chip. The grid layout's cards play the recorded
   *  provisioning run when clicked ("recorded run"); the hero layout's
   *  other examples are peeks at staged work ("staged in this room") — the
   *  chip must describe what the click actually does. */
  otherChip?: string;
  hero?: boolean;
  /** A peeked other-example: its finished work is on the page behind the
   *  panel right now, and the card says so. */
  pressed?: boolean;
  /** Absent -> the card is informational: same face, no button semantics. */
  onPick?: () => void;
}) {
  const face = (
    <>
      {/* Every card says what KIND of moment it is — a walkthrough finding was
          "why does one card have a chip and the other two nothing? what do the
          others do?". */}
      {card.live_build ? (
        <span className={DEMO_LIVE_CHIP}>{liveChip}</span>
      ) : (
        <span className={DEMO_RECORDED_CHIP}>{otherChip}</span>
      )}
      <span
        className={
          hero
            ? "block text-[15px] leading-snug font-semibold"
            : "block text-[13px] leading-snug font-semibold"
        }
      >
        {card.title}
      </span>
      {card.tagline ? (
        <span className="mt-1 block text-[12px] leading-snug text-[var(--demo-muted)]">
          {card.tagline}
        </span>
      ) : null}
      {pressed ? (
        <span className="mt-1.5 block text-[11px] font-medium text-[var(--demo-accent)]">
          Open behind this panel
        </span>
      ) : null}
    </>
  );
  if (!onPick) return <div className={DEMO_CARD}>{face}</div>;
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={pressed || undefined}
      className={`${DEMO_CARD}${hero ? " block w-full" : ""}${
        pressed ? " ring-2 ring-[var(--demo-accent)]/60" : ""
      }`}
    >
      {face}
    </button>
  );
}

export function DemoFirstRunRail({
  payload,
  welcome,
  subdomain,
  userKey,
  clientName = "",
  bookingUrl = "",
  hasTour = false,
  mode = "auto",
  onClose,
}: {
  payload: FirstRunPayload;
  /** The demo's existing welcome line (`me.demo.welcome`), already rendered
   *  server-side. Empty -> the arc opens on the first orient beat. */
  welcome: string;
  subdomain: string;
  userKey: string;
  /** Who the (fictional) client is, for the copy that speaks their name.
   *  `payload.context.client_name` wins; the chrome passes
   *  `me.demo.display_name` as the fallback, so the beats read correctly even
   *  for a payload authored before `context` existed. */
  clientName?: string;
  /** The demo's booking link (`me.demo.booking_url`) — the closing beat's
   *  conversion action. Empty/invalid -> the button simply isn't offered. */
  bookingUrl?: string;
  /** Whether this demo carries a guided tour — the closing beat must not
   *  offer "Show me around" when the click would dismiss into nothing. */
  hasTour?: boolean;
  /** "auto" = the seat's real first run. "replay" = they asked to see it
   *  again: no marks, no beacons, no live build. */
  mode?: FirstRunMode;
  /** Called on dismissal, so the mount point can unmount a replay. */
  onClose?: () => void;
}) {
  const router = useRouter();
  const reduced = useMemo(prefersReducedMotion, []);
  const timeline = useMemo(
    () => buildTimeline(payload, { hasWelcome: welcome.trim().length > 0 }),
    [payload, welcome],
  );
  const isReplayMode = mode === "replay";
  const recording = payload.live_build_recording;
  /** The name the arc speaks. The payload's own value wins so the platform's
   *  authored lines and the rail's generated ones can never disagree. */
  const client = (payload.context?.client_name || clientName || "").trim();

  const [state, setState] = useState<FirstRunState | null>(() => firstState(timeline));
  const [chosen, setChosen] = useState<FirstRunCard | null>(null);
  /** Whether the `choose` beat's other examples are expanded. Closed by
   *  default: one workflow leads, the rest are available rather than offered. */
  const [showOthers, setShowOthers] = useState(false);
  /** Replay lines revealed so far. In reduced-motion the whole log is revealed
   *  the moment the state opens. */
  const [shown, setShown] = useState(0);
  const [live, setLive] = useState<LiveBuild>({ phase: "idle" });
  /** Reveal sub-phase: the pack is open on screen behind the panel. The
   *  reveal used to push the artifact AND advance in one click — and the
   *  very next beat (proof) navigates back to the working canvas, so the
   *  deck the arc had just bragged about was yanked away before it rendered
   *  (a real walkthrough caught it: "says a deck is composed but never
   *  shows it"). Now "Open the pack" only opens; the prospect reads the
   *  deck for as long as they like and moves on with their own click. */
  const [packOpened, setPackOpened] = useState(false);
  /** Ask sub-phase: the question is sitting in the REAL composer. Same
   *  lesson — prefill-navigate-and-advance in one click left the rail
   *  narrating the next beat while the prospect was still finding where
   *  their question went. Now "Try it" stages the question (and the
   *  composer flashes to show where it landed) and the rail waits. */
  const [askTried, setAskTried] = useState(false);
  /** Ask sub-phase 2: the question has actually been SENT — by the rail's
   *  own Send, or by the composer's. The forward action is gated on it, so
   *  "keep going" can never walk a prospect past a question that was only
   *  ever staged (the point of the beat is watching it answered). */
  const [askSent, setAskSent] = useState(false);
  /** How "Try it" handed the question over (./machine.ts `askHandoff`):
   *  staged into a composer the prospect can see, or sent directly through
   *  the agent panel when there is none — a narrow window, a closed panel,
   *  a composer the rail itself covers. Decided on the click, by measuring. */
  const [handoff, setHandoff] = useState<AskHandoff | null>(null);
  /** A direct hand-off nobody picked up in time. */
  const [askStalled, setAskStalled] = useState(false);
  /** The panel itself — measured against the composer it may be covering. */
  const panelRef = useRef<HTMLElement | null>(null);
  /** The prompt well in the ask beat — the fly-to-composer animation's
   *  launch pad. */
  const promptWellRef = useRef<HTMLParagraphElement | null>(null);

  // Sub-phases belong to one visit of their state: leaving (or re-entering
  // via Back) resets them, so the reveal always opens pack-closed and the
  // ask always opens untried.
  /** Which other-example card was last PEEKED at (its work is behind the
   *  panel). Belongs to one visit of `choose`, like the other sub-phases. */
  const [peeked, setPeeked] = useState<string | null>(null);

  useEffect(() => {
    if (state !== "reveal") setPackOpened(false);
    if (state !== "choose") setPeeked(null);
    if (state !== "ask") {
      setAskTried(false);
      setAskSent(false);
      setHandoff(null);
      setAskStalled(false);
    }
  }, [state]);

  // A direct hand-off parks the send for whichever composer mounts; if none
  // has sent it by the time the park expires, say so and let them go on.
  useEffect(() => {
    if (state !== "ask" || handoff !== "direct" || !askTried || askSent) return;
    const timer = window.setTimeout(() => setAskStalled(true), SEND_TTL_MS + 1_000);
    return () => window.clearTimeout(timer);
  }, [state, handoff, askTried, askSent]);

  // Hear the send, whichever button fired it: the rail's own Send drives the
  // composer through the bus, and a prospect can just as well press the
  // composer's send (or edit the question first and send that) — the beat
  // moves on when a send truly happened, not when a particular button was
  // clicked.
  useEffect(() => {
    if (state !== "ask" || !askTried) return;
    return onComposerSent(() => setAskSent(true));
  }, [state, askTried]);

  /** Fly the prompt from the rail's well into the real composer — so the
   *  reader's eye TRAVELS with the question instead of it teleporting into
   *  a box they weren't looking at (a real walkthrough read the old cut as
   *  "a jerky refresh, then a message just appears"). A fixed-position
   *  clone of the well animates to the composer's rect; the prefill (which
   *  focuses and flashes the composer — see ChatComposer) fires when it
   *  lands, so the glow reads as the arrival. Any missing piece — reduced
   *  motion, no composer on screen, no WAAPI — degrades to the immediate
   *  prefill. */
  const flyPromptToComposer = useCallback(
    (text: string) => {
      const land = () => prefillComposer(text);
      try {
        if (reduced) return land();
        const from = promptWellRef.current;
        const target = document.querySelector<HTMLElement>("[data-composer-input]");
        if (!from || !target) return land();
        const a = from.getBoundingClientRect();
        const b = target.getBoundingClientRect();
        if (a.width < 1 || b.width < 40 || b.height < 10) return land();
        const ghost = document.createElement("div");
        ghost.textContent = text;
        const look = window.getComputedStyle(from);
        Object.assign(ghost.style, {
          position: "fixed",
          left: `${a.left}px`,
          top: `${a.top}px`,
          width: `${a.width}px`,
          boxSizing: "border-box",
          padding: look.padding,
          borderRadius: look.borderRadius,
          border: look.border,
          background: look.backgroundColor,
          color: look.color,
          font: look.font,
          margin: "0",
          zIndex: "200",
          pointerEvents: "none",
          willChange: "transform, opacity",
        });
        document.body.appendChild(ghost);
        const scale = Math.min(1, b.width / a.width);
        const anim = ghost.animate(
          [
            { transform: "translate(0, 0) scale(1)", opacity: 1 },
            {
              transform: `translate(${b.left - a.left}px, ${b.top - a.top}px) scale(${scale})`,
              opacity: 0.7,
            },
          ],
          { duration: 640, easing: "cubic-bezier(0.22, 0.9, 0.24, 1)" },
        );
        const finish = () => {
          ghost.remove();
          land();
        };
        anim.onfinish = finish;
        anim.oncancel = finish;
      } catch {
        land(); // no WAAPI / measuring threw — the text still arrives
      }
    },
    [reduced],
  );

  const openedAt = useRef(Date.now());
  const logRef = useRef<HTMLDivElement | null>(null);
  /** One POST per arc. React runs effects twice in dev StrictMode, and this
   *  one spends the demo's hourly build budget. */
  const buildFired = useRef(false);
  /** The chosen card, readable from the beacon effect without adding it to
   *  that effect's deps (which would re-fire a beacon per pick). */
  const chosenRef = useRef<FirstRunCard | null>(null);
  /** One "see other examples" beacon per arc, however often it is toggled. */
  const othersBeaconed = useRef(false);

  const isLive = chosen?.live_build === true;

  // One auto-open counted per mount (the attempt cap lives in ./seen.ts, so a
  // prospect who refreshes mid-arc resumes but one who ignores it isn't nagged).
  // Deliberately mount-only (empty deps): this counts OPENINGS, not state
  // changes — re-running it per transition would burn the cap inside one arc.
  // A replay counts nothing; ./seen.ts enforces that, not this call site.
  useEffect(() => {
    noteFirstRunAttempt(subdomain, userKey, mode);
  }, [subdomain, userKey, mode]);

  // Publish "a rail is on screen" while there is a state to show — the
  // welcome banner stands down on this signal in BOTH modes, and the ripple's
  // "What if?" card while any guide is up (see ../guideOverlays.ts).
  const onScreen = state !== null;
  useEffect(() => {
    setGuideOpen("rail", onScreen);
    return () => setGuideOpen("rail", false);
  }, [onScreen]);

  // ONE GUIDE AT A TIME. Opening, the rail tells the tour to close; and when
  // the tour opens — from the sidebar's "Show me around" or this panel's own —
  // the rail dismisses, the same way skipping out of the arc does. Before
  // this, a tour started anywhere but the closing button played UNDER the
  // rail: the panel sat over the Artifacts list the tour's Review step 1 was
  // spotlighting (Oxera, filmed).
  useEffect(() => {
    if (onScreen) announceGuideOpened("rail");
  }, [onScreen]);
  const dismissRef = useRef<() => void>(() => undefined);
  useEffect(() => onGuideOpened("rail", () => dismissRef.current()), []);

  // Instrumentation: one beacon per state FIRST reached, carrying the state
  // name and the ms since the arc opened. Reuses the existing demo-engagement
  // channel (POST /demo/events, kind "first_run") — the ops plane reads it
  // beside session_start / opened_hero / tour_done. First-reach only, because
  // the arc now has a Back control: re-entering a beat is re-reading, not
  // progress, and a second beacon would corrupt both the funnel's counts and
  // its time-to-state percentiles. A replay is silent: the server's context
  // whitelist can't carry a "this was a replay" tag, so the funnel would
  // double-count the same prospect (see ./seen.ts).
  const beaconed = useRef<Set<FirstRunState>>(new Set());
  useEffect(() => {
    if (state === null || !firstRunReports(mode)) return;
    if (beaconed.current.has(state)) return;
    beaconed.current.add(state);
    postDemoEvent("first_run", {
      state,
      ms: String(Date.now() - openedAt.current),
      // `live` marks the replay beacon of an arc whose replay was a real
      // build, so the funnel can tell the two apart without a second kind.
      ...(state === "replay" && chosenRef.current?.live_build === true ? { live: "1" } : {}),
    });
  }, [state, mode]);

  /** Once per arc: the closing button dismisses AND starts the tour, whose
   *  opening then asks the rail to close again — one completion beacon, not
   *  two. */
  const dismissed = useRef(false);
  const dismiss = useCallback(() => {
    if (dismissed.current) return;
    dismissed.current = true;
    setState(null);
    markFirstRunSeen(subdomain, userKey, mode);
    onClose?.();
  }, [subdomain, userKey, mode, onClose]);
  dismissRef.current = dismiss;

  const go = useCallback(
    (target: FirstRunState | null) => {
      if (target === null) {
        dismiss();
        return;
      }
      setState(target);
    },
    [dismiss],
  );

  const advance = useCallback(() => {
    if (state === null) return;
    go(nextState(timeline, state));
  }, [go, state, timeline]);

  /** Step back one beat — re-reading, not progress: `null` at the start
   *  (the button isn't rendered there), and beacons only ever count the
   *  first reach, so walking back and forward again is funnel-invisible. */
  const back = useCallback(() => {
    if (state === null) return;
    const target = prevState(timeline, state);
    if (target !== null) go(target);
  }, [go, state, timeline]);

  const skip = useCallback(() => {
    if (state === null) return;
    go(skipTarget(timeline, state));
  }, [go, state, timeline]);

  /** Runtime strings from the demo payload are unknowable to the route
   *  typegen, so navigate through history like the guided tour does. The
   *  payload guard has already refused anything that isn't root-relative. */
  const push = useCallback(
    (href: string) => {
      if (!href) return;
      try {
        router.history.push(href);
      } catch {
        /* malformed path — stay put rather than breaking the arc */
      }
    },
    [router],
  );

  // ANCHOR THE ARC. The opening beats speak about the page behind the panel
  // — "Behind this panel is the Q3 platform review", "this board pack" —
  // which was only true by luck: a first login lands on the hero page, but
  // a replay starts from wherever the prospect happens to be (audited live:
  // the pain beat said "this board pack" over the workspace directory).
  // One navigation on open makes the deixis true on every path; a no-op
  // when they are already there.
  const arcAnchored = useRef(false);
  useEffect(() => {
    if (arcAnchored.current || state === null) return;
    arcAnchored.current = true;
    const href = payload.reveal?.href ?? "";
    if (href && window.location.pathname !== href) push(href);
  }, [state, payload.reveal?.href, push]);

  // ── the live build ────────────────────────────────────────────────────────

  // ONE POST per arc, fired on entering `replay`. Deliberately not cancelled
  // on cleanup: the build happens server-side the moment the request lands,
  // so dropping the answer (React's dev StrictMode double-mount would) would
  // spend the demo's one build and show nothing for it.
  //
  // A REPLAY never posts at all — re-running someone's build because they
  // wanted to re-read the intro would spend a real budget on a re-read. It
  // goes straight to the recording, or says why there isn't one.
  useEffect(() => {
    if (state !== "replay" || !isLive || buildFired.current) return;
    buildFired.current = true;
    if (isReplayMode) {
      setLive(recording ? { phase: "recorded" } : { phase: "failed" });
      return;
    }
    setLive({ phase: "running" });
    runLiveBuild().then(
      (result) =>
        // A cached build is not a build. When the platform kept the first real
        // run, every later seat watches THAT rather than reading one line
        // about it; with no recording, the old cached notice still stands.
        setLive(
          result.cached
            ? recording
              ? { phase: "recorded" }
              : { phase: "cached", result }
            : { phase: "done", result },
        ),
      // A failed build (rate-limit window spent, transient 5xx — the reasons
      // don't reach a prospect) falls back to the RECORDING when one exists:
      // it is the real run that built the artifact on screen, which beats the
      // provisioning log by every measure the arc cares about. Only with no
      // recording does the state degrade to the log, and say so.
      () => setLive(recording ? { phase: "recorded" } : { phase: "failed" }),
    );
  }, [state, isLive, isReplayMode, recording]);

  // ── what the replay state is showing ──────────────────────────────────────

  /** The live answer's outcome, put through the SAME guard the recording's
   *  went through (./payload.ts): api/demo.ts declares the field's shape but
   *  validates nothing, and a half-written stat must not reach the card.
   *  Memoised on `live` so the parse doesn't hand back a new object every
   *  render — the reveal's navigation effect keys off whether one exists. */
  const liveOutcome = useMemo(
    () =>
      live.phase === "done" || live.phase === "cached"
        ? parseFirstRunOutcome(live.result.outcome)
        : null,
    [live],
  );

  /** The finished build on screen, whichever way it got there. Collapsing the
   *  live result and the recording into one shape is what lets a single
   *  presentation render both — which is the point: a recorded run must look
   *  like the run it is a recording of, and say which it is in words. */
  const built: {
    steps: readonly FirstRunRecordedStep[];
    title: string;
    href: string;
    totalMs: number;
    /** True only for a build that ran because this prospect clicked. */
    fresh: boolean;
    recorded: boolean;
    /** What the run CONCLUDED, when the server said. Null keeps the old
     *  provenance-first reveal. */
    outcome: FirstRunOutcome | null;
  } | null =
    live.phase === "done"
      ? {
          steps: live.result.steps,
          title: live.result.artifact_title,
          href: live.result.href,
          totalMs: live.result.total_ms,
          fresh: !live.result.cached,
          recorded: false,
          outcome: liveOutcome,
        }
      : live.phase === "recorded" && recording
        ? {
            steps: recording.steps,
            title: recording.artifact_title,
            href: recording.href,
            totalMs: recording.total_ms,
            fresh: false,
            recorded: true,
            outcome: recording.outcome,
          }
        : live.phase === "cached"
          ? {
              // The file exists and the reveal should open it — but the LOG
              // is not this result's probe steps (two lines of cache
              // bookkeeping): the cached state renders the provisioning
              // replay instead, so `steps` stays empty on purpose.
              steps: [],
              title: live.result.artifact_title,
              href: live.result.href,
              totalMs: live.result.total_ms,
              fresh: false,
              recorded: false,
              outcome: liveOutcome,
            }
          : null;

  /** The outcome card's material. A build's OWN answer wins (it is the run
   *  on screen); the payload reveal's server-computed outcome is the
   *  fallback that keeps the outcome-first reveal alive in replay mode and
   *  after a reconcile drops the recording — the two paths that used to
   *  degrade to the statless provenance screen. Null only for demos with no
   *  live-build facts at all, which keep the old reveal untouched. */
  const outcome = built?.outcome ?? payload.reveal?.outcome ?? null;
  const hasOutcome = outcome !== null;

  // Curated at render, not at parse: the payload module stays a verbatim
  // validator, and the curation (dropping ops noise, translating work events
  // into layman lines) is a presentation rule — see ./replayLines.ts.
  // The CHOSEN card's own run (./machine.ts `replayFor`) — not the whole
  // feed, which merged every baked run into every card's "The run".
  const replaySource = replayFor(payload, chosen);
  const replayLines = useMemo(() => curateReplayLines(replaySource), [replaySource]);
  const liveSteps = built?.steps ?? [];
  /** True while this arc's replay state IS the build (live or recorded). A
   *  failed build drops back to the recorded provisioning log, so it is false
   *  from then on. */
  const liveActive = isLive && live.phase !== "failed" && live.phase !== "cached";
  const lineCount = liveActive ? liveSteps.length : replayLines.length;
  /** A recorded run is paced by its OWN measurements — the shape of the real
   *  run survives the replay — while the live path keeps its flat reading
   *  stagger (its steps arrive already complete, all at once). */
  const recordedDelays = useMemo(
    () => (built?.recorded ? recordedStepDelaysMs(built.steps) : null),
    [built?.recorded, built?.steps],
  );
  const intervalMs = liveActive ? LIVE_STEP_STAGGER_MS : replayIntervalMs(replayLines.length);
  const linesRevealed = shown >= lineCount;
  // A live build is not "done" until the server has answered, however few
  // lines are on screen — the forward action must not offer the deliverable
  // before there is one.
  const replayDone = liveActive ? built !== null && linesRevealed : linesRevealed;

  useEffect(() => {
    if (state !== "replay") return;
    if (reduced) {
      setShown(lineCount); // instant-complete: no pacing animation
      return;
    }
    setShown(lineCount > 0 ? 1 : 0);
  }, [state, reduced, lineCount]);

  useEffect(() => {
    if (state !== "replay" || reduced) return;
    if (shown >= lineCount) return;
    // The recording's own per-step delay for the step about to appear, or the
    // flat cadence for everything else.
    const delay = recordedDelays?.[shown] ?? intervalMs;
    const timer = window.setTimeout(() => setShown((n) => Math.min(n + 1, lineCount)), delay);
    return () => window.clearTimeout(timer);
  }, [state, reduced, shown, lineCount, intervalMs, recordedDelays]);

  // Keep the newest line in view as the log fills. `shown` is the trigger —
  // the scroll target is a ref, which React can't track.
  useEffect(() => {
    if (state !== "replay" || shown === 0) return;
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [state, shown]);

  // On entering `reveal`, deep-link to the hero deliverable — or, when a build
  // ran (or was recorded), to the artifact it made. Falls back to the chosen
  // card's own destination when the payload named no reveal href.
  //
  // UNLESS there is an outcome to read first. Then the reveal is its own
  // screen — the numbers and the decision — and the deliverable opens on the
  // prospect's own click ("Open the pack"), rather than swapping the app out
  // from under the sentence they are still reading. Every path without an
  // outcome keeps the automatic navigation exactly as it was.
  const revealHref = built?.href || payload.reveal?.href || chosen?.href || "";
  useEffect(() => {
    if (state !== "reveal" || hasOutcome) return;
    push(revealHref);
  }, [state, revealHref, push, hasOutcome]);

  // The proof beat plays over the WORKING PAGE (`payload.reveal.href` is the
  // hero canvas): the evidence markers the beat invites a click on are
  // rendered in canvas prose, not on the artifact preview. This navigation
  // is the deliberate return trip from the pack — it fires only when the
  // prospect advances INTO proof (the reveal's dwell phase means they chose
  // to move on), and it is a no-op when they are already on the canvas.
  useEffect(() => {
    if (state !== "proof") return;
    push(payload.reveal?.href ?? "");
  }, [state, payload.reveal?.href, push]);

  // ── keyboard ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (state === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.key === "Escape") {
        e.preventDefault();
        skip();
      } else if (e.key === "Enter") {
        // `choose` has no single default action — the prospect picks a card.
        if (state === "choose") return;
        // The ask beat's forward action is gated on the question actually
        // having been sent; Enter must not walk past it either.
        if (state === "ask" && !askSent && !askStalled) return;
        e.preventDefault();
        if (state === "replay" && !replayDone) {
          // Enter fast-forwards the log — but a live build has nothing to
          // fast-forward TO until the server has answered, and Enter must
          // never advance past a deliverable that doesn't exist yet.
          if (!reduced) setShown(lineCount);
          return;
        }
        advance();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [state, skip, advance, replayDone, reduced, lineCount, askSent, askStalled]);

  if (state === null) return null;

  const total = stepCount(timeline);
  const idx = stepIndex(timeline, state);
  /** The scripted states, one progress dot each (the closing strip is the
   *  exit, not a step) — keyed by state name rather than by index. */
  const dots = timeline.filter((s) => s !== "closing");

  // ── per-state body ────────────────────────────────────────────────────────

  let eyebrow = "First run";
  let body: ReactNode = null;
  /** The panel's single forward action. `null` for states whose body carries
   *  the action itself (`choose`). `ghost` demotes it visually — the closing
   *  beat's dismiss must not outshine the booking link above it. */
  let action: { label: string; onClick: () => void; ghost?: boolean } | null = null;
  /** The ask beat's one line, and whether the panel shrinks to it (a direct
   *  hand-off: the answer is playing in the assistant panel, and on a narrow
   *  screen a full-height rail would sit on top of it). */
  let askLine = "";
  let askCompact = false;

  if (state === "welcome") {
    eyebrow = "Welcome";
    body = (
      <>
        <p className="text-[17px] leading-snug font-semibold">{welcome}</p>
        {expectLineHost(timeline) === "welcome" && payload.context?.expect_line ? (
          // What they are about to GET, said before anything is shown — a
          // prospect should never be one beat ahead of their own
          // understanding of what this demo contains. When there is a problem
          // beat this sentence belongs to IT, as the release of the tension
          // the pain line just set; here it would be released early, before
          // there is anything to release.
          <p className="mt-2.5 text-[13.5px] leading-normal text-[var(--demo-muted)]">
            {payload.context.expect_line}
          </p>
        ) : null}
        {payload.context?.product_line ? (
          // ONE quiet line of grounding on the arc's first screen: what
          // Chaidojo is. The Godin doctrine demoted the product explanation
          // to the sign-in page — and a real walkthrough found that a
          // prospect who arrived past that page (or skimmed it) met a wall
          // of numbers with no idea whose product this was. A caption, not a
          // beat: the welcome stays the headline.
          <p className="mt-2 text-[13px] leading-normal text-[var(--demo-muted)]">
            {payload.context.product_line}
          </p>
        ) : null}
      </>
    );
    action = { label: "Show me", onClick: advance };
  } else if (state === "problem") {
    // The opening beat: their problem, then the release. No product, no
    // client name, no files — one recurring pain a prospect recognises as
    // theirs, and one sentence saying what they are about to get instead.
    eyebrow = "The problem";
    body = (
      <>
        <p className="text-[17px] leading-snug font-semibold">{payload.context?.pain_line}</p>
        {payload.context?.expect_line ? (
          <p className="mt-2.5 text-[13.5px] leading-normal text-[var(--demo-muted)]">
            {payload.context.expect_line}
          </p>
        ) : null}
        {timeline[0] === "problem" && payload.context?.product_line ? (
          // The same one-line grounding the welcome carries — here only when
          // the problem beat IS the arc's first screen (no welcome message).
          <p className="mt-2 text-[13px] leading-normal text-[var(--demo-muted)]">
            {payload.context.product_line}
          </p>
        ) : null}
      </>
    );
    action = {
      // Point at whatever actually comes next: the files when this demo has
      // them, otherwise the work itself. "What WE loaded" — matching the next
      // step's own title; a button that says "you" leading to a screen that
      // says "we" reads as two different people talking.
      label: nextState(timeline, "problem") === "inputs" ? "What we loaded" : "Show me",
      onClick: advance,
    };
  } else if (state === "what") {
    // Beat one: what the product is, and whose data this is pretending to be.
    // The fiction line is at reading size, not in a footnote — disclosing that
    // the client is invented is a thing to say plainly, and a prospect who
    // finds it out later has been handled rather than told.
    eyebrow = "What this is";
    body = (
      <>
        <p className="text-[17px] leading-snug font-semibold">{payload.context?.product_line}</p>
        {payload.context?.fiction_line ? (
          <p className="mt-2.5 text-[13.5px] leading-normal text-[var(--demo-muted)]">
            {payload.context.fiction_line}
          </p>
        ) : null}
      </>
    );
    action = {
      // Same conditional as `problem`'s: a demo with `context` but no files
      // has no inputs screen to promise.
      label: nextState(timeline, "what") === "inputs" ? "What we loaded" : "Show me",
      onClick: advance,
    };
  } else if (state === "inputs") {
    // Beat two: the actual files. Before this, "your data room" was an
    // assertion; now it is a list the prospect can read.
    eyebrow = "What we loaded";
    const files = payload.files;
    const visible = files.slice(0, FIRST_RUN_MAX_FILES);
    // The ROOM's count, not the list's: the platform sends a screenful of
    // names and the whole-room number beside them (payload `file_count`),
    // the same number any `{file_count}` in the opening lines was filled
    // with.
    const rest = payload.file_count - visible.length;
    body = (
      <>
        <p className="text-[15px] leading-snug font-semibold">
          We loaded {payload.file_count} of {possessive(client, "these files")}.
        </p>
        {/* A fixed-height scrollable table, NOT a growing list: fourteen
            described files stacked as chips pushed the panel past the
            viewport (filmed). The panel keeps its size; the files scroll. */}
        <div className="mt-3 max-h-[240px] overflow-y-auto rounded-lg border border-[var(--demo-border)] [scrollbar-width:thin]">
          <table className="w-full border-collapse text-[12px]">
            <tbody>
              {visible.map((file, i) => (
                <tr
                  key={`${i}-${file.name}`}
                  className="border-b border-[var(--demo-border)] last:border-0"
                >
                  <td className="w-[44%] px-2.5 py-1.5 align-top">
                    <span className="break-all">{file.name}</span>
                    {file.ext ? (
                      <span className="ml-1.5 inline-block rounded-[3px] border border-[var(--demo-border)] px-1 align-[1px] font-mono text-[10px] tracking-wide text-[var(--demo-muted)] uppercase">
                        {file.ext}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-2.5 py-1.5 align-top leading-snug text-[var(--demo-muted)]">
                    {file.description}
                  </td>
                </tr>
              ))}
              {rest > 0 ? (
                <tr>
                  <td colSpan={2} className="px-2.5 py-1.5 text-[var(--demo-muted)]">
                    and {rest} more
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {/* The provenance note. Three cases, one sentence each, and the
            invented-data disclosure is said exactly ONCE across the arc:
              - the fiction line hosts here: the authored line (which already
                says invented/illustrative in the author's own words) plus a
                SHORT anchor tying the files to it. The old continuation
                restated "realistic shapes, illustrative numbers" after an
                authored line that had just said the same thing — a visible
                double disclaimer (filmed on a live demo).
              - the fiction line was spoken by the `what` beat: the note is
                the standalone reminder it always was.
              - NO fiction line at all: this demo may be mirroring a REAL firm
                off its own published material (a supported authoring case),
                so the note must not assert the data is invented. It says
                where the numbers come from and nothing more. */}
        <p className="mt-2.5 text-[11.5px] leading-snug text-[var(--demo-muted)]">
          {fictionLineHost(timeline) === "inputs" && payload.context?.fiction_line ? (
            <>{endStopped(payload.context.fiction_line)} The files above were invented with it.</>
          ) : payload.context?.fiction_line ? (
            <>
              All of these were invented for this demo — realistic shapes, illustrative numbers.
              Nothing here is a real company&rsquo;s data.
            </>
          ) : (
            <>These are the files everything you&rsquo;re about to see was built from.</>
          )}
        </p>
      </>
    );
    action = { label: "Got it", onClick: advance };
  } else if (state === "choose") {
    const pick = (card: FirstRunCard) => {
      setChosen(card);
      // Synchronously, so the `replay` beacon that fires on the very next
      // transition can already see which card this is.
      chosenRef.current = card;
      advance();
    };
    // ONE workflow leads. A menu of three asks a prospect to compare things
    // they have no basis to compare, at the exact moment the arc should be
    // carrying them; the live-build card is the one worth watching, so it
    // takes the screen and the rest wait behind a quiet toggle. With no live
    // card there is no hero to lead with, and the old grid stands.
    const hero = payload.cards.find((card) => card.live_build) ?? null;
    const others = hero ? payload.cards.filter((card) => card !== hero) : payload.cards;
    const recorded = payload.live_build_recording !== null;
    // The live chip per path: a recording beats everything (that is what
    // will play); a replay without one plays the provisioning log, so it is
    // a recorded run, not a build; only a real first arc promises the build.
    const liveChip = recorded ? "the run, recorded" : isReplayMode ? "recorded run" : "builds it live";
    eyebrow = hero ? "What we'll run" : "Pick one";
    body = hero ? (
      <>
        <p className="text-[15px] leading-snug font-semibold">
          The work we&rsquo;ll run on those files.
        </p>
        <div className="mt-3">
          <ChooseCard card={hero} liveChip={liveChip} hero onPick={() => pick(hero)} />
        </div>
        {others.length > 0 ? (
          <div className="mt-2.5">
            <button
              type="button"
              className={DEMO_SUBTLE_BTN}
              aria-expanded={showOthers}
              onClick={() => {
                // Which prospects even look is the one signal the informational
                // cards still carry — beacon the first open (real arcs only, so
                // a replay doesn't inflate the count).
                if (!showOthers && !othersBeaconed.current && firstRunReports(mode)) {
                  othersBeaconed.current = true;
                  postDemoEvent("cta_click", { path: "first-run-see-others" });
                }
                setShowOthers((open) => !open);
              }}
            >
              {showOthers ? "Hide other examples" : `See other examples (${others.length})`}
            </button>
            {showOthers ? (
              <>
                {/* PEEKS, not picks. The arc downstream of here — promise,
                    reveal, repeat — is staged around the hero workflow, and
                    a pick that advanced into the hero's story under another
                    card's name was a fake choice (audited once). But a card
                    that did NOTHING was read as broken (audited twice —
                    "choosing either doesn't work"). So clicking one opens
                    its FINISHED WORK on the page behind the panel — the
                    honest thing a staged workflow can show — and the card
                    says so; the guided build still continues from the hero
                    above. */}
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {others.map((card) => {
                    const key = card.workflow_id || card.href;
                    return (
                      <ChooseCard
                        key={key}
                        card={card}
                        liveChip={liveChip}
                        otherChip="staged in this room"
                        pressed={peeked === key}
                        onPick={() => {
                          if (card.href) push(card.href);
                          setPeeked(key);
                          if (firstRunReports(mode)) {
                            postDemoEvent("cta_click", {
                              path: "first-run-peek",
                              value: card.workflow_id,
                            });
                          }
                        }}
                      />
                    );
                  })}
                </div>
                <p className="mt-2 text-[11.5px] leading-snug text-[var(--demo-muted)]">
                  These run the same way, on the same files — click one to peek at its work
                  behind this panel. The guided build continues from the card above.
                </p>
              </>
            ) : null}
          </div>
        ) : null}
      </>
    ) : (
      <>
        <p className="text-[15px] leading-snug font-semibold">
          Pick what you&rsquo;d like to watch it make from those files.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          {payload.cards.map((card) => (
            <ChooseCard
              key={card.workflow_id || card.href}
              card={card}
              liveChip={liveChip}
              onPick={() => pick(card)}
            />
          ))}
        </div>
      </>
    );
  } else if (state === "promise") {
    eyebrow = chosen?.title || "Here's the plan";
    body = (
      <>
        {payload.promise?.line ? (
          <p className="text-[15px] leading-snug font-semibold">{payload.promise.line}</p>
        ) : null}
        {payload.promise?.pages.length ? (
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {payload.promise.pages.map((page, i) => (
              <li key={`${i}-${page}`} className={DEMO_CHIP}>
                {page}
              </li>
            ))}
          </ul>
        ) : null}
      </>
    );
    action = { label: "Watch", onClick: advance };
  } else if (state === "replay") {
    eyebrow = built?.recorded
      ? "The run, recorded"
      : liveActive
        ? live.phase === "done"
          ? "Built just now"
          : "Building it now"
        : "The run";
    body = (
      <>
        <p className="text-[13px] leading-snug text-[var(--demo-muted)]">
          {built?.recorded
            ? recordedKicker(client)
            : liveActive
              ? // Past tense only once the server has answered; a claim about
                // a finished build must not sit above a still-running one.
                live.phase === "done"
                ? LIVE_KICKER
                : LIVE_RUNNING_KICKER
              : replayKicker(client)}
          {isLive && live.phase === "failed" ? (
            <span className="mt-1 block text-[var(--demo-text)]/75">
              {isReplayMode ? REPLAY_FALLBACK : LIVE_FALLBACK}
            </span>
          ) : null}
        </p>
        <div
          ref={logRef}
          // aria-live so a screen reader hears the log fill rather than
          // silently watching it; "polite" keeps it out of the way.
          aria-live="polite"
          aria-busy={!replayDone}
          className={`mt-3 max-h-[168px] ${DEMO_LOG}`}
        >
          {liveActive ? (
            <>
              {liveSteps.slice(0, shown).map((step, i) => (
                <div key={`${i}-${step.name}`} className="flex items-start gap-2">
                  <span className="mt-[3px] flex size-3 shrink-0 items-center justify-center">
                    <CheckIcon className="size-3 text-[var(--demo-accent)]" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1 break-words text-[var(--demo-text)]/85">
                    {step.label || step.name}
                  </span>
                  {/* The server's own measurement of the step it just ran —
                      or, for a recording, of the step it ran that day. A step
                      that cost nothing (the cache hit) shows no duration
                      rather than a suspiciously fast one. */}
                  {step.ms > 0 ? (
                    <span className="shrink-0 tabular-nums text-[var(--demo-muted)]">
                      {secs(step.ms)}
                    </span>
                  ) : null}
                </div>
              ))}
              {live.phase === "idle" || live.phase === "running" ? (
                <div className="flex items-start gap-2">
                  <span className="mt-[3px] flex size-3 shrink-0 items-center justify-center">
                    <span
                      className="size-1.5 rounded-full bg-[var(--demo-muted)] motion-safe:animate-pulse"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="min-w-0 break-words text-[var(--demo-text)]/85">
                    composing your deliverable&hellip;
                  </span>
                </div>
              ) : null}
            </>
          ) : (
            replayLines.slice(0, shown).map((entry, i) => {
              const complete = i < shown - 1 || replayDone;
              return (
                <div key={`${entry.t}-${i}`} className="flex items-start gap-2">
                  <span className="mt-[3px] flex size-3 shrink-0 items-center justify-center">
                    {complete ? (
                      <CheckIcon className="size-3 text-[var(--demo-accent)]" aria-hidden="true" />
                    ) : (
                      <span
                        className="size-1.5 rounded-full bg-[var(--demo-muted)] motion-safe:animate-pulse"
                        aria-hidden="true"
                      />
                    )}
                  </span>
                  <span className="min-w-0 break-words text-[var(--demo-text)]/85">
                    {entry.line}
                  </span>
                </div>
              );
            })
          )}
        </div>
        {built && liveActive ? (
          <p className="mt-2 text-[12px] leading-snug text-[var(--demo-muted)]">
            {built.recorded
              ? `${built.title} — built in ${secs(built.totalMs)}. You're watching the recording.`
              : built.fresh
                ? `${built.title} — built in ${secs(built.totalMs)}.`
                : `${built.title} was already built from these figures.`}
          </p>
        ) : live.phase === "cached" && built ? (
          // The cache line, now BENEATH a log of real work rather than
          // instead of one: the provisioning run above is what stocked this
          // room, and the deck itself was built earlier from the same
          // figures. One flat sentence here used to be the whole beat.
          <p className="mt-2 text-[12px] leading-snug text-[var(--demo-muted)]">
            {built.title} was already built from these figures — above is the run that stocked
            this room.
          </p>
        ) : null}
      </>
    );
    if (liveActive && built === null) {
      // Nothing to skip to: the deliverable does not exist yet, and the arc
      // must not offer to reveal one.
      action = null;
    } else {
      action = replayDone
        ? {
            // With an outcome to read, the next screen is the FINDING, and
            // the deliverable waits for the prospect's own click there.
            label: hasOutcome ? "See what it found" : "Show me what it made",
            onClick: () => {
              // Without one, the reveal is LITERAL: the deliverable that did
              // not exist a moment ago takes the screen behind the rail as
              // the reveal line says so — the navigation is the staging. A
              // recording lands on the same artifact, which is still there.
              if (!hasOutcome && built?.href) push(built.href);
              advance();
            },
          }
        : { label: "Skip ahead", onClick: () => setShown(lineCount) };
    }
  } else if (state === "reveal") {
    // The provenance sentence. It is the same claim in both layouts and stays
    // path-honest — a fresh build, a recorded one and a plain reveal each say
    // a different, true thing. What changes is its RANK: leading the screen
    // when there is nothing else to lead with, a caption under the numbers
    // when there is.
    const provenance = built?.fresh
      ? `${built.title} — this file did not exist when you clicked.`
      : built?.recorded
        ? `${built.title}, built from ${possessive(client, "these files")} — this is the run that made it.`
        : chosen?.title
          ? `${chosen.title} — built from ${possessive(client, "your own files")}, not a template.`
          : `Built from ${possessive(client, "your own files")}, not a template.`;
    const pageChips = payload.reveal?.pages ?? [];
    eyebrow = outcome ? "What it found" : "The deliverable";
    body = outcome ? (
      <>
        <div className={DEMO_WELL}>
          {outcome.stats.length > 0 ? (
            <dl
              className={`grid grid-cols-2 gap-x-5 gap-y-3 ${
                STAT_COLUMNS[outcome.stats.length] ?? "sm:grid-cols-4"
              }`}
            >
              {outcome.stats.map((stat, i) => (
                <div key={`${i}-${stat.label}`} className="min-w-0">
                  <dt className="truncate text-[10.5px] font-medium tracking-[0.06em] text-[var(--demo-muted)] uppercase">
                    {stat.label}
                  </dt>
                  <dd className="mt-1 text-[18px] leading-none font-semibold tabular-nums">
                    {stat.value}
                  </dd>
                  {/* The delta carries its own sign, so the colour is a
                      second channel and never the only one. */}
                  {stat.delta ? (
                    <dd className={`mt-1 text-[11.5px] leading-snug ${DEMO_DELTA[stat.sentiment]}`}>
                      {stat.delta}
                    </dd>
                  ) : null}
                </div>
              ))}
            </dl>
          ) : null}
          {outcome.decision ? (
            <p
              className={
                outcome.stats.length > 0
                  ? "mt-3 border-t border-[var(--demo-border)] pt-2.5 text-[14px] leading-snug font-semibold"
                  : "text-[14px] leading-snug font-semibold"
              }
            >
              {outcome.decision}
            </p>
          ) : null}
        </div>
        <p className="mt-2.5 text-[11.5px] leading-snug text-[var(--demo-muted)]">{provenance}</p>
        {pageChips.length > 0 ? (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {pageChips.map((page) => (
              <button
                key={page.href}
                type="button"
                onClick={() => push(page.href)}
                className={DEMO_CHIP_BUTTON_SM}
              >
                {page.label}
              </button>
            ))}
          </div>
        ) : null}
      </>
    ) : (
      <>
        <p className="text-[15px] leading-snug font-semibold">{provenance}</p>
        {pageChips.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {pageChips.map((page) => (
              <button
                key={page.href}
                type="button"
                onClick={() => push(page.href)}
                className={DEMO_CHIP_BUTTON}
              >
                {page.label}
              </button>
            ))}
          </div>
        ) : null}
      </>
    );
    // Click 5b, in TWO clicks now. "Open the pack" used to also advance —
    // and the next beat's own navigation immediately pulled the prospect off
    // the deck, so the file the arc had just composed was never actually
    // seen. The pack opens and the rail WAITS; the second click is theirs.
    // The destination prefers the build this arc just ran, then the
    // /me-injected address of the deliverable that already exists (the
    // replay-mode fix: a replay fires no build, and without this the deck
    // never showed — audited live), then the working page. The label calls
    // the file what it is, and a deliverable is only claimed when an
    // artifact is genuinely the destination. The dwell also runs on the
    // OUTCOME-LESS reveal whenever there is a deliverable to open — an
    // authored-hero demo has no outcome, and its arc used to narrate
    // "Built … (DOCX)" and then advance right past the file.
    const packHref = built?.href || payload.reveal?.pack_href || "";
    const packFormat = built?.href ? "pptx" : payload.reveal?.pack_format || "";
    const packLabel = packHref ? openPackLabel(packFormat) : "Open the page";
    action =
      (outcome && revealHref) || packHref
        ? packOpened
          ? {
              label: nextState(timeline, "reveal") === "proof" ? "Now check its work" : "Keep going",
              onClick: advance,
            }
          : {
              label: packLabel,
              onClick: () => {
                push(packHref || revealHref);
                setPackOpened(true);
              },
            }
        : { label: "One more thing", onClick: advance };
  } else if (state === "proof") {
    // The trust beat — the ladder's "can I trust it?" rung, answered with a
    // REAL click rather than a claim. The rail has no scrim, so the page
    // behind is genuinely interactive; the beat waits while they try it and
    // advances on their say-so. No new data, no new surface: it points at the
    // evidence markers the product already renders on every cited figure.
    eyebrow = "Check its work";
    body = (
      <>
        <p className="text-[15px] leading-snug font-semibold">
          Don&rsquo;t take its word for it.
        </p>
        <p className="mt-2.5 text-[13.5px] leading-normal text-[var(--demo-muted)]">
          This is the working page the numbers came from. Every figure carries a small raised
          marker — click one, and it names the exact file the number was read from, and the date
          it was true.
        </p>
      </>
    );
    action = {
      // The same handrail idiom as `problem` -> "What we loaded": name the
      // next beat when it is the ask, otherwise a plain continue.
      label: nextState(timeline, "proof") === "ask" ? "Now ask it something" : "Keep going",
      onClick: advance,
    };
  } else if (state === "ask") {
    eyebrow = "Now ask it something";
    const beat = askBeat({ handoff, tried: askTried, sent: askSent, stalled: askStalled });
    askLine = beat.line;
    askCompact = beat.compact;
    body = (
      <>
        <p className="text-[13px] leading-snug text-[var(--demo-muted)]">{beat.line}</p>
        <p
          ref={promptWellRef}
          className="mt-2.5 rounded-lg border border-[var(--demo-border)] bg-[var(--demo-raised)] px-3 py-2 text-[14px] leading-snug text-[var(--demo-text)]/90"
        >
          {payload.ask?.prompt}
        </p>
      </>
    );
    const prompt = payload.ask?.prompt ?? "";
    const askHref = payload.ask?.href ?? "";
    /** No box to show: open the agent panel — at any width — and send the
     *  question through whichever composer it mounts (composerBus
     *  `sendThroughComposer`, pinned to the ask page). */
    const handOffDirect = (navigating: boolean) => {
      setHandoff("direct");
      requestAgentPanel();
      sendThroughComposer(prompt, askHref);
      // A panel request made before the destination's shell existed (from
      // a page outside it) would be lost — ask again once it has mounted.
      if (navigating) window.setTimeout(requestAgentPanel, 450);
    };
    // Staged, three clicks, each honest about what it does: stage the
    // question (it flies into the real composer), SEND it (the same send the
    // composer's own button performs — and sending from the composer counts
    // just the same), and only then keep going. The old single click staged,
    // navigated and advanced at once, which read as "a jerky refresh, then a
    // message just appears". Direct, when no composer is visible: "Try it"
    // asks, and the beat waits for the send to land.
    action =
      beat.action === "keep"
        ? { label: "Keep going", onClick: advance }
        : beat.action === "send"
          ? {
              label: "Send the question",
              onClick: () => {
                if (requestComposerSubmit()) return;
                // The box went away since it was lit (panel closed, tab
                // switched, window narrowed, page left): hand the question
                // over directly rather than do nothing.
                if (!prompt) return;
                const away = !!askHref && window.location.pathname !== askHref;
                if (away) push(askHref);
                handOffDirect(away);
              },
            }
          : beat.action === "try"
            ? {
                label: "Try it",
                onClick: () => {
                  // Only navigate when we are somewhere else: re-pushing the
                  // path we are already on drops the ?conversation search
                  // param and remounts the thread — the "jerky page refresh".
                  const alreadyThere = !askHref || window.location.pathname === askHref;
                  if (!alreadyThere) push(askHref);
                  setAskTried(true);
                  if (!prompt) return;
                  const composer = document.querySelector<HTMLElement>("[data-composer-input]");
                  const mode = askHandoff(rectOf(composer), rectOf(panelRef.current), {
                    width: window.innerWidth,
                    height: window.innerHeight,
                  });
                  if (mode === "direct") {
                    handOffDirect(!alreadyThere);
                    return;
                  }
                  setHandoff("staged");
                  if (alreadyThere) {
                    flyPromptToComposer(prompt);
                  } else {
                    // Give the destination's panel a beat to mount, then
                    // fly; the helper degrades to an instant prefill if the
                    // composer still isn't there.
                    window.setTimeout(() => flyPromptToComposer(prompt), 450);
                  }
                },
              }
            : null;
  } else if (state === "repeat") {
    // The last scripted beat, and the one that turns a demo into a purchase:
    // what they watched happen once, drawn as the standing job it would be.
    const repeat = payload.repeat;
    eyebrow = "And then it keeps running";
    body = (
      <>
        <p className="text-[15px] leading-snug font-semibold">
          You watched it run once. This is what it looks like running on its own.
        </p>
        <div className={`mt-3 ${DEMO_WELL}`}>
          <p className="text-[13.5px] leading-snug font-semibold">{repeat?.workflow_title}</p>
          <p className="mt-1 text-[12px] leading-snug text-[var(--demo-muted)]">
            {REPEAT_PIPELINE}
          </p>
          {repeat?.cadence ? (
            // DISPLAY ONLY — a picture of a schedule, not a control. Nothing
            // here is clickable and nothing here books anything, so these are
            // not radios and carry no radio semantics: the glyphs are
            // decorative, and which one is chosen is said in words a screen
            // reader can read instead of "black circle".
            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] leading-snug">
              <span>
                <span aria-hidden="true" className="mr-1.5 text-[var(--demo-accent)]">
                  &#9679;
                </span>
                <span className="sr-only">Set to run: </span>
                {repeat.cadence}
              </span>
              <span className="text-[var(--demo-muted)]">
                <span aria-hidden="true" className="mr-1.5">
                  &#9675;
                </span>
                <span className="sr-only">Not selected: </span>
                On demand
              </span>
            </div>
          ) : null}
          {repeat?.owner_name || repeat?.output_title ? (
            <dl className="mt-2.5 border-t border-[var(--demo-border)] pt-2.5 text-[12px] leading-snug">
              {repeat?.owner_name ? (
                <div className="flex gap-2">
                  <dt className="w-14 shrink-0 text-[var(--demo-muted)]">Owner</dt>
                  <dd className="min-w-0">{repeat.owner_name}</dd>
                </div>
              ) : null}
              {repeat?.output_title ? (
                <div className="mt-1 flex gap-2">
                  <dt className="w-14 shrink-0 text-[var(--demo-muted)]">Output</dt>
                  <dd className="min-w-0">{repeat.output_title}</dd>
                </div>
              ) : null}
            </dl>
          ) : null}
        </div>
        {/* Only qualify a schedule that is actually on screen. */}
        {repeat?.cadence ? (
          <p className="mt-2.5 text-[11.5px] leading-snug text-[var(--demo-muted)]">
            {REPEAT_HONESTY}
          </p>
        ) : null}
      </>
    );
    action = { label: "One last thing", onClick: advance };
  } else {
    // closing — the HANDOFF, reachable by skip from anywhere. The old exit
    // strip was two unexplained buttons under an internal codeword ("That's
    // the arc" — a word the demo never says out loud) with Done as the
    // loudest control on screen; the demo's own author looked at it and
    // asked what it was trying to convey. The last screen of a demo owes a
    // cold reader three things, in order: closure (the scripted part is
    // over), possession (this room is yours now, and nothing breaks), and
    // exits that each say in one plain line what they are for — with the
    // CONVERSION as the one orange button, never the dismissal.
    eyebrow = "That's the demo";
    const booking = /^https?:\/\//i.test(bookingUrl) ? bookingUrl : "";
    body = (
      <>
        <p className="text-[15px] leading-snug font-semibold">This room is now yours.</p>
        <p className="mt-1.5 text-[13px] leading-normal text-[var(--demo-muted)]">
          Stay and explore — read the pack, ask more questions, click any number. Nothing here
          breaks.
        </p>
        <div className="mt-3 flex flex-col gap-2">
          {booking ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <a
                href={booking}
                target="_blank"
                rel="noreferrer noopener"
                onClick={() =>
                  postDemoEvent("cta_click", {
                    path: isReplayMode ? "first-run-book-replay" : "first-run-book",
                  })
                }
                className={`${DEMO_PRIMARY_BTN} inline-flex items-center gap-1.5`}
              >
                Book a walkthrough
                <ExternalLinkIcon className="size-3" aria-hidden="true" />
              </a>
              <span className="text-[11.5px] leading-snug text-[var(--demo-muted)]">
                Talk to the team that set this room up.
              </span>
            </div>
          ) : null}
          {payload.receipt_url ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <a
                href={payload.receipt_url}
                target="_blank"
                rel="noreferrer noopener"
                // "Receipt" is our word, not a prospect's; the caption says
                // what the page is FOR. The beacon keeps its path so the
                // funnel is continuous — except in replay, where it says so
                // rather than inflating the count.
                onClick={() =>
                  postDemoEvent("cta_click", {
                    path: isReplayMode ? "first-run-receipt-replay" : "first-run-receipt",
                  })
                }
                className={`${DEMO_CHIP_BUTTON} inline-flex items-center gap-1.5 py-1.5`}
              >
                Share a one-page summary
                <ExternalLinkIcon className="size-3" aria-hidden="true" />
              </a>
              <span className="text-[11.5px] leading-snug text-[var(--demo-muted)]">
                What was built and checked — no login needed, made for forwarding.
              </span>
            </div>
          ) : null}
          {hasTour ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {/* Named to MATCH the sidebar launcher — "See how it's made"
                  and "Show me around" were two labels for the same tour.
                  Rendered only when a tour exists: this click used to
                  dismiss into nothing on a tour-less demo. */}
              <button
                type="button"
                className={`${DEMO_CHIP_BUTTON} py-1.5`}
                onClick={() => {
                  dismiss();
                  startDemoTour();
                }}
              >
                Show me around
              </button>
              <span className="text-[11.5px] leading-snug text-[var(--demo-muted)]">
                The guided version of everything else — also in the sidebar, any time.
              </span>
            </div>
          ) : null}
        </div>
      </>
    );
    action = { label: "Explore on my own", onClick: dismiss, ghost: true };
  }

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[139] flex justify-center p-4"
      // The panel is a complementary aside, not a modal: the app behind it
      // stays live so the reveal and ask states can point at real screens.
      role="complementary"
      aria-label="Demo first run"
      style={demoChromeVars}
    >
      <section
        ref={panelRef}
        className={`pointer-events-auto w-[680px] max-w-[calc(100vw-2rem)] ${DEMO_PANEL} motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2`}
      >
        {askCompact ? (
          // ONE LINE while a direct hand-off plays in the assistant panel:
          // on a narrow screen the panel opens under this rail, and a
          // full-height card would cover the answer it says to watch.
          <div className="flex items-center gap-3 px-4 py-2.5">
            <span className={DEMO_BADGE}>Demo guide</span>
            <p
              className="min-w-0 flex-1 text-[12.5px] leading-snug text-[var(--demo-text)]/85"
              aria-live="polite"
            >
              {askLine}
            </p>
            {action ? (
              <button type="button" className={DEMO_PRIMARY_BTN} onClick={action.onClick}>
                {action.label}
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <header className="flex items-center gap-3 border-b border-[var(--demo-border)] px-5 py-2.5">
              {/* Stated, not implied: this panel is the demo talking. */}
              <span className={DEMO_BADGE}>Demo guide</span>
              <span className={DEMO_EYEBROW}>{eyebrow}</span>
              {total > 1 && idx > 0 ? (
                <span className="flex items-center gap-1" aria-label={`Step ${idx} of ${total}`}>
                  {dots.map((dot, i) => (
                    <span
                      key={dot}
                      className={`size-1.5 rounded-full ${
                        i < idx ? "bg-[var(--demo-accent)]" : "bg-[var(--demo-border)]"
                      }`}
                      aria-hidden="true"
                    />
                  ))}
                </span>
              ) : null}
              <span className="flex-1" />
              <button type="button" onClick={skip} className={DEMO_SUBTLE_BTN}>
                {state === "closing" ? "Close" : "Skip"}
              </button>
            </header>
            <div className="px-5 py-4">
              {body}
              {(() => {
                // The footer row: Back on the left (ghost, absent on the first
                // beat), the state's single forward action on the right. Back
                // renders even for states whose body carries the action itself
                // (`choose`) — re-reading the files before picking is exactly
                // the moment someone reaches for it.
                const canGoBack = prevState(timeline, state) !== null;
                if (!action && !canGoBack) return null;
                return (
                  <div className="mt-4 flex items-center justify-between gap-2">
                    {canGoBack ? (
                      <button type="button" className={DEMO_SUBTLE_BTN} onClick={back}>
                        Back
                      </button>
                    ) : (
                      <span />
                    )}
                    {action ? (
                      <button
                        type="button"
                        className={action.ghost ? DEMO_GHOST_BTN : DEMO_PRIMARY_BTN}
                        onClick={action.onClick}
                      >
                        {action.label}
                      </button>
                    ) : null}
                  </div>
                );
              })()}
            </div>
          </>
        )}
      </section>
    </div>,
    document.body,
  );
}

/** Sidebar affordance: replay the intro. Mirrors DemoTourLauncher, and is
 *  rendered only for demos that actually carry a first-run payload. */
export function DemoFirstRunLauncher({ collapsed }: { collapsed?: boolean }) {
  return (
    <button
      type="button"
      onClick={startDemoFirstRun}
      title="Replay the intro"
      aria-label="Replay the intro"
      className={
        collapsed
          ? "mx-auto flex size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          : "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
      }
    >
      <ReplayIcon />
      {!collapsed && <span>Replay the intro</span>}
    </button>
  );
}

function ReplayIcon() {
  // Inline (lucide RotateCcw geometry) so this file stays dependency-light,
  // matching DemoTourLauncher's own inline icon.
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}
